import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorSeverity } from "../../../error-handling/error-handler";
import { errorUI } from "../../../error-handling/index";
import type { MainService } from "../../../main-service";
import { formatShortJapaneseDate } from "../../../utils/date-formatter";
import { useSessionActions } from "../../hooks/useSessionActions";
import { UI_ICONS } from "../../icons";
import { Modal } from "./Modal";

import { SessionDetailView } from "./SessionDetailView";

interface HistoryModalProps {
	isOpen: boolean;
	onClose: () => void;
	service: MainService | null;
	currentSessionId?: string | null;
}

import type { Session } from "../../../types/core";

/**
 * Formats milliseconds into m:ss format.
 * Performance optimization: Avoid toString() and padStart() in render loops
 * by using a simple conditional for zero-padding.
 */
const formatDuration = (ms: number) => {
	const mins = Math.floor(ms / 60000);
	const secs = Math.floor((ms % 60000) / 1000);
	return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

interface HistoryItemProps {
	session: Session;
	isCurrent: boolean;
	isSelected: boolean;
	onSelect: (id: string) => void;
	onClick: (session: Session) => void;
	onToggleFavorite: (e: React.MouseEvent, session: Session) => void;
	onResume: (e: React.MouseEvent, id: string) => void;
	onDelete: (e: React.MouseEvent, id: string) => void;
}

/**
 * Individual history session item, memoized to prevent re-rendering the entire list
 * when only selection or one item changes.
 */
const HistoryItem: React.FC<HistoryItemProps> = memo(
	({
		session,
		isCurrent,
		isSelected,
		onSelect,
		onClick,
		onToggleFavorite,
		onResume,
		onDelete,
	}) => {
		return (
			// biome-ignore lint/a11y/useSemanticElements: Div used for layout
			<div
				className={`flex items-center gap-4 p-4 bg-bg-main border rounded-xl cursor-pointer transition-all duration-200 group relative hover:border-primary hover:bg-primary/5 hover:translate-x-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-[0.98] active:translate-x-0 animate-in fade-in slide-in-from-left-5 duration-300 ${
					isCurrent
						? "border-primary ring-1 ring-primary/20 bg-primary/5"
						: session.isFavorite
							? "border-amber-500/30"
							: "border-border-main"
				} ${isSelected ? "ring-2 ring-primary ring-inset bg-primary/5" : ""}`}
				onClick={() => onClick(session)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						onClick(session);
					}
				}}
				role="button"
				tabIndex={0}
			>
				<div className="shrink-0 flex items-center">
					<input
						type="checkbox"
						className="w-5 h-5 cursor-pointer accent-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded"
						checked={isSelected}
						onChange={() => onSelect(session.id)}
						onClick={(e) => e.stopPropagation()}
						aria-label={`${session.title || "無題のセッション"}を選択`}
					/>
				</div>
				<div className="flex-1 min-w-0">
					<div className="font-semibold text-text-main truncate mb-1 flex items-center gap-2">
						{session.title || "無題のセッション"}
						{isCurrent && (
							<span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary text-white animate-pulse">
								進行中
							</span>
						)}
					</div>
					<div className="flex items-center gap-4 text-[13px] text-text-dim">
						<span className="flex items-center gap-1">
							{UI_ICONS.DATE} {formatShortJapaneseDate(session.startTime)}
						</span>
						<span className="flex items-center gap-1">
							{UI_ICONS.DURATION}{" "}
							{formatDuration(session.metrics.totalDuration)}
						</span>
						<span className="flex items-center gap-1">
							{UI_ICONS.COMMENT} {session.metrics.commentCount}
						</span>
					</div>
				</div>
				<div className="flex gap-1 shrink-0">
					<button
						type="button"
						className={`p-2 rounded-lg bg-white/5 border border-transparent transition-all cursor-pointer flex items-center justify-center hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95 ${
							session.isFavorite
								? "text-amber-500"
								: "text-text-dim hover:text-text-main"
						}`}
						onClick={(e) => onToggleFavorite(e, session)}
						title={
							session.isFavorite
								? `「${session.title || "無題"}」のお気に入り解除`
								: `「${session.title || "無題"}」をお気に入りに追加`
						}
						aria-label={
							session.isFavorite
								? `「${session.title || "無題"}」のお気に入り解除`
								: `「${session.title || "無題"}」をお気に入りに追加`
						}
					>
						<span role="img" aria-hidden="true">
							{session.isFavorite
								? UI_ICONS.FAVORITE_ACTIVE
								: UI_ICONS.FAVORITE_INACTIVE}
						</span>
					</button>
					<button
						type="button"
						className={`p-2 rounded-lg bg-white/5 border border-transparent transition-all flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95 ${
							isCurrent
								? "opacity-20 cursor-not-allowed text-text-dim"
								: "cursor-pointer text-primary hover:bg-primary/10 hover:border-primary/20"
						}`}
						onClick={(e) => onResume(e, session.id)}
						disabled={isCurrent}
						title={
							isCurrent ? "進行中のセッションです" : "このセッションを再開"
						}
						aria-label={
							isCurrent ? "進行中のセッションです" : "このセッションを再開"
						}
					>
						<span role="img" aria-hidden="true">
							▶️
						</span>
					</button>
					<button
						type="button"
						className={`p-2 rounded-lg bg-white/5 border border-transparent transition-all flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95 ${
							isCurrent
								? "opacity-20 cursor-not-allowed text-text-dim"
								: "cursor-pointer text-text-dim hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/20"
						}`}
						onClick={(e) => onDelete(e, session.id)}
						disabled={isCurrent}
						title={
							isCurrent
								? "進行中のセッションは削除できません"
								: `セッション「${session.title || "無題"}」を削除`
						}
						aria-label={
							isCurrent
								? "進行中のセッションは削除できません"
								: `セッション「${session.title || "無題"}」を削除`
						}
					>
						<span role="img" aria-hidden="true">
							{UI_ICONS.DELETE}
						</span>
					</button>
				</div>
			</div>
		);
	},
);

export const HistoryModal: React.FC<HistoryModalProps> = ({
	isOpen,
	onClose,
	service,
	currentSessionId,
}) => {
	const [sessions, setSessions] = useState<Session[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const searchInputRef = useRef<HTMLInputElement>(null);
	const [showFavoriteOnly, setShowFavoriteOnly] = useState(false);
	const [selectedSession, setSelectedSession] = useState<Session | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const { toggleFavorite } = useSessionActions({ service });
	const rootRef = useRef<HTMLDivElement>(null);
	const listScrollPosRef = useRef(0);

	const loadHistory = useCallback(async () => {
		if (service) {
			const history = await service.getHistory();

			// Performance optimization: Fast-path for already sorted chronological streams.
			// This reduces O(N log N) to O(N) check and maintains reference stability.
			// Using .getTime() for direct numeric comparison to avoid implicit valueOf() overhead.
			let isSorted = true;
			let prevTime = history.length > 0 ? history[0].startTime.getTime() : 0;
			for (let i = 1; i < history.length; i++) {
				const currentTime = history[i].startTime.getTime();
				if (prevTime < currentTime) {
					isSorted = false;
					break;
				}
				prevTime = currentTime;
			}

			if (isSorted) {
				setSessions(history);
				return;
			}

			// Performance optimization: Sort by start time descending using direct numeric comparison.
			// This avoids O(N log N) redundant Date object allocations.
			// Also using a spread copy to avoid in-place mutation of service-provided data.
			setSessions(
				[...history].sort(
					(a, b) => b.startTime.getTime() - a.startTime.getTime(),
				),
			);
		}
	}, [service]);

	useEffect(() => {
		if (isOpen && service) {
			loadHistory();
			setSelectedSession(null); // Reset detail view when opened
			setSelectedIds(new Set()); // Reset selection when opened
			listScrollPosRef.current = 0; // Reset scroll memory when modal is freshly opened
		}
	}, [isOpen, service, loadHistory]);

	// Scroll management when switching between list and detail view
	useEffect(() => {
		const container = rootRef.current?.parentElement;
		if (!container) return;

		if (selectedSession) {
			// Save current scroll position before detail view renders
			// Note: In some cases, we might want to save this in the click handler
			// but since we are already in the effect, we use the last known position
			// or we can rely on the fact that container.scrollTop is still the list's position
			// just after the state change but before the DOM updates significantly.
			// Actually, it's safer to save it in the click handler if we want precision.
			container.scrollTop = 0;
		} else {
			// Restore scroll position when returning to list
			container.scrollTop = listScrollPosRef.current;
		}
	}, [selectedSession]);

	const handleSessionClick = useCallback((session: Session) => {
		const container = rootRef.current?.parentElement;
		if (container) {
			listScrollPosRef.current = container.scrollTop;
		}
		setSelectedSession(session);
	}, []);

	const handleSessionUpdate = useCallback((updatedSession: Session) => {
		setSelectedSession(updatedSession);
		setSessions((prev) =>
			prev.map((s) => (s.id === updatedSession.id ? updatedSession : s)),
		);
	}, []);

	const handleBack = useCallback(() => {
		setSelectedSession(null);
		loadHistory();
	}, [loadHistory]);

	const handleToggleFavorite = useCallback(
		async (e: React.MouseEvent, session: Session) => {
			e.stopPropagation();
			await toggleFavorite({
				sessionId: session.id,
				currentIsFavorite: Boolean(session.isFavorite),
				onUpdated: (nextIsFavorite) => {
					// Update local state to reflect change immediately
					setSessions((prev) =>
						prev.map((s) =>
							s.id === session.id ? { ...s, isFavorite: nextIsFavorite } : s,
						),
					);
				},
			});
		},
		[toggleFavorite],
	);

	const handleResume = useCallback(
		async (e: React.MouseEvent, id: string) => {
			e.stopPropagation();
			if (service && id !== currentSessionId) {
				await service.resumeExistingSession(id);
				onClose();
			}
		},
		[service, currentSessionId, onClose],
	);

	const handleDelete = useCallback(
		async (e: React.MouseEvent, id: string) => {
			e.stopPropagation();

			if (id === currentSessionId) {
				errorUI.showNotification(
					"進行中のセッションは削除できません。終了してから削除してください。",
					ErrorSeverity.MEDIUM,
				);
				return;
			}

			if (service) {
				errorUI.showConfirmation(
					"このセッションを削除してもよろしいですか？",
					async () => {
						await service.deleteSession(id);
						setSelectedIds((prev) => {
							const next = new Set(prev);
							next.delete(id);
							return next;
						});
						loadHistory();
						errorUI.showNotification(
							"セッションを削除しました。",
							ErrorSeverity.LOW,
						);
					},
				);
			}
		},
		[service, currentSessionId, loadHistory],
	);

	const handleBulkDelete = useCallback(async () => {
		if (service && selectedIds.size > 0) {
			const hasCurrentSession = selectedIds.has(currentSessionId || "");

			if (hasCurrentSession) {
				errorUI.showNotification(
					"選択範囲に進行中のセッションが含まれています。進行中のセッションを除いてから削除してください。",
					ErrorSeverity.MEDIUM,
				);
				return;
			}

			errorUI.showConfirmation(
				`選択した ${selectedIds.size} 件のセッションを削除してもよろしいですか？`,
				async () => {
					await service.deleteSessions(Array.from(selectedIds));
					setSelectedIds(new Set());
					loadHistory();
					errorUI.showNotification(
						"セッションを一括削除しました。",
						ErrorSeverity.LOW,
					);
				},
			);
		}
	}, [service, selectedIds, currentSessionId, loadHistory]);

	const filteredSessions = useMemo(() => {
		// Performance optimization: Fast-path for empty filters.
		// This returns the original array reference, providing O(1) complexity
		// and maintaining reference stability for the list component.
		if (!searchQuery && !showFavoriteOnly) {
			return sessions;
		}

		const query = searchQuery.toLowerCase();
		return sessions.filter((session) => {
			// Optimization: Early return for non-favorites if filtering for favorites.
			// This avoids O(N) string operations in the search check below.
			if (showFavoriteOnly && !session.isFavorite) {
				return false;
			}
			// Optimization: Skip search check if query is empty.
			if (!query) {
				return true;
			}
			return (session.title || "").toLowerCase().includes(query);
		});
	}, [sessions, searchQuery, showFavoriteOnly]);

	// Performance optimization: Memoize selection state to avoid O(N) scans on every render.
	const isAllSelected = useMemo(() => {
		// Fast-path: Check sizes first. If selected count is less than visible count,
		// it's impossible for all visible items to be selected.
		// This provides O(1) complexity for most partial selection interactions.
		if (
			filteredSessions.length === 0 ||
			selectedIds.size < filteredSessions.length
		) {
			return false;
		}

		return filteredSessions.every((s) => selectedIds.has(s.id));
	}, [filteredSessions, selectedIds]);

	const toggleSelectAll = useCallback(() => {
		if (isAllSelected) {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				for (const s of filteredSessions) {
					next.delete(s.id);
				}
				return next;
			});
		} else {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				for (const s of filteredSessions) {
					next.add(s.id);
				}
				return next;
			});
		}
	}, [filteredSessions, isAllSelected]);

	const toggleSelect = useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	return (
		<Modal
			title={selectedSession ? "詳細" : "履歴"}
			isOpen={isOpen}
			onClose={onClose}
		>
			<div className="flex flex-col h-full min-h-0" ref={rootRef}>
				{selectedSession ? (
					<SessionDetailView
						session={selectedSession}
						service={service}
						currentSessionId={currentSessionId}
						onSessionUpdate={handleSessionUpdate}
						onBack={handleBack}
						onClose={onClose}
					/>
				) : (
					<>
						<div className="flex items-center gap-3 mb-6 flex-wrap">
							<div className="flex items-center gap-2 pr-3 border-r border-border-main">
								<input
									type="checkbox"
									className="w-5 h-5 cursor-pointer accent-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded"
									checked={isAllSelected}
									onChange={toggleSelectAll}
									title="すべて選択"
									aria-label="すべてのセッションを選択"
								/>
								{selectedIds.size > 0 && (
									<button
										type="button"
										className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-500 border border-red-500/20 rounded-md cursor-pointer text-sm font-semibold transition-all hover:bg-red-500 hover:text-white active:scale-95 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none"
										onClick={handleBulkDelete}
										title="選択したセッションを削除"
									>
										{UI_ICONS.DELETE} 削除 ({selectedIds.size})
									</button>
								)}
							</div>
							<div className="flex-1 min-w-[200px] relative">
								<span
									className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim text-sm pointer-events-none"
									role="img"
									aria-hidden="true"
								>
									{UI_ICONS.SEARCH}
								</span>
								<input
									ref={searchInputRef}
									type="text"
									className="w-full pl-9 pr-10 py-2 bg-bg-main border border-border-main rounded-lg text-text-main text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
									placeholder="セッションを検索..."
									aria-label="セッションを検索"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
								{searchQuery && (
									<button
										type="button"
										className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-md transition-colors active:scale-95"
										onClick={() => {
											setSearchQuery("");
											searchInputRef.current?.focus();
										}}
										aria-label="検索をクリア"
									>
										{UI_ICONS.CLOSE}
									</button>
								)}
							</div>
							<button
								type="button"
								className={`p-2.5 rounded-lg border transition-all cursor-pointer text-xl flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95 ${
									showFavoriteOnly
										? "bg-amber-500/10 border-amber-500/50 text-amber-500"
										: "bg-bg-main border-border-main text-text-dim hover:border-text-dim"
								}`}
								onClick={() => setShowFavoriteOnly((prev) => !prev)}
								title={showFavoriteOnly ? "すべて表示" : "お気に入りのみ表示"}
								aria-label={
									showFavoriteOnly ? "すべて表示" : "お気に入りのみ表示"
								}
							>
								<span role="img" aria-hidden="true">
									{showFavoriteOnly
										? UI_ICONS.FAVORITE_ACTIVE
										: UI_ICONS.FAVORITE_INACTIVE}
								</span>
							</button>
						</div>

						<div className="flex flex-col gap-3 min-h-0 overflow-y-auto pr-1 flex-1">
							{filteredSessions.length === 0 ? (
								<div className="py-20 text-center text-text-dim opacity-60 flex flex-col items-center gap-3 animate-in fade-in duration-500">
									<span
										className="text-4xl opacity-20"
										role="img"
										aria-hidden="true"
									>
										{searchQuery ? UI_ICONS.SEARCH : UI_ICONS.HISTORY}
									</span>
									<p className="font-semibold mb-1">
										{searchQuery
											? `「${searchQuery}」に一致するセッションはありません`
											: showFavoriteOnly
												? "お気に入りのセッションはありません"
												: "履歴がありません"}
									</p>
									{searchQuery && (
										<button
											type="button"
											className="mt-2 px-4 py-2 bg-white/5 border border-border-main rounded-lg text-xs font-semibold text-text-main hover:bg-white/10 transition-all active:scale-95 cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
											onClick={() => {
												setSearchQuery("");
												searchInputRef.current?.focus();
											}}
										>
											検索をクリア
										</button>
									)}
								</div>
							) : (
								filteredSessions.map((session) => (
									<HistoryItem
										key={session.id}
										session={session}
										isCurrent={session.id === currentSessionId}
										isSelected={selectedIds.has(session.id)}
										onSelect={toggleSelect}
										onClick={handleSessionClick}
										onToggleFavorite={handleToggleFavorite}
										onResume={handleResume}
										onDelete={handleDelete}
									/>
								))
							)}
						</div>
					</>
				)}
			</div>
		</Modal>
	);
};
